import { useNavigate } from "react-router-dom";

import { BaseScore } from "apps/user/learner/api/types";
import useFeatureFlags, {
  FEATURE_FLAGS,
} from "apps/user/learner/hooks/use-feature-flags";
import useWebOriEnabled from "apps/user/learner/hooks/use-web-ori-enabled";
import {
  setOriStarterMessage,
  wasStarterSentRecently,
} from "apps/user/learner/lib/ori-starter-message";
import {
  buildTapIn,
  TapIn,
  TapInContext,
  TapInEntryPoint,
} from "apps/user/learner/lib/tap-in";
import { useTranslation } from "lib/i18n/use-translation";

/** Score, previous score and band for a score the screen shows. */
export const scoreContext = (
  score: BaseScore
): Pick<TapInContext, "score" | "previous_score" | "band_key"> => {
  if (score.value == null) return {};
  const difference = Number.parseInt(score.scoreDifference ?? "", 10);
  return {
    score: score.value,
    ...(Number.isNaN(difference)
      ? {}
      : { previous_score: score.value - difference }),
    ...(score.labelKey ? { band_key: score.labelKey } : {}),
  };
};

export const indexTapIn = (
  entryPoint: TapInEntryPoint,
  overallIndex: BaseScore
): TapIn =>
  buildTapIn(entryPoint, { surface: "index", ...scoreContext(overallIndex) });

/**
 * Tap-in plumbing shared by the web entry points: whether it may show
 * (the company's Ori entitlement plus `ori_tap_in`), the learner's message,
 * and opening the chat with it queued.
 */
export const useTapIn = () => {
  const { isEnabled, resolved: flagsResolved } = useFeatureFlags();
  const { enabled: oriEnabled } = useWebOriEnabled();
  const navigate = useNavigate();
  const { t } = useTranslation(["components"]);

  const enabled = oriEnabled && isEnabled(FEATURE_FLAGS.ORI_TAP_IN);
  const messageFor = (tapIn: TapIn) => t(tapIn.messageKey, tapIn.messageValues);

  /** Returns false when the message was sent inside the cooldown and is not resent. */
  const open = (tapIn: TapIn): boolean => {
    const text = messageFor(tapIn);
    const queued = !wasStarterSentRecently(text);
    if (queued) setOriStarterMessage(text, tapIn.entryPoint, tapIn.context);
    navigate("/user/chat");
    return queued;
  };

  return { enabled, flagsResolved, messageFor, open };
};
